import { supabase } from '../lib/supabase';

/**
 * Notes internes, generiques : une meme API pour chaque table de notes
 *   extra_notes     (extra_id)      — extras / credits
 *   directive_notes (directive_id)  — directives
 * Les deux tables ont exactement la meme forme.
 *
 * Visibles par l'equipe du projet (RLS par project_id), jamais dans le PDF ni
 * dans le CSV. Chacun ne modifie ou ne supprime que SES notes : la regle est
 * verifiee ici (created_by = utilisateur courant) en plus de la base.
 */

export const NOTE_MAX_LENGTH = 2000;

const currentUserId = async () => {
  try {
    const { data } = await supabase?.auth?.getUser();
    return data?.user?.id || null;
  } catch {
    return null;
  }
};

// Noms affiches des auteurs (user_profiles.full_name), en une requete.
const authorNames = async (userIds) => {
  const ids = [...new Set((userIds || [])?.filter(Boolean))];
  if (!ids?.length) return {};
  const { data, error } = await supabase?.from('user_profiles')?.select('id, full_name')?.in('id', ids);
  if (error) return {};
  return (data || [])?.reduce((acc, row) => { acc[row?.id] = row?.full_name || null; return acc; }, {});
};

const withAuthors = async (rows) => {
  const names = await authorNames(rows?.map((r) => r?.created_by));
  return rows?.map((r) => ({ ...r, authorName: names?.[r?.created_by] || null }));
};

const cleanBody = (body) => {
  const text = String(body || '')?.trim();
  if (!text) throw new Error('NOTE_EMPTY');
  if (text?.length > NOTE_MAX_LENGTH) throw new Error('NOTE_TOO_LONG');
  return text;
};

// Fabrique : un service par table de notes (fk = colonne de l'entite).
const makeNotesService = (table, fk) => {
  const NOTE_SELECT = `id, ${fk}, project_id, body, created_by, created_at, updated_at`;

  /** Fil complet d'une entite, plus recent d'abord. */
  const list = async (entityId) => {
    if (!entityId) return [];
    const { data, error } = await supabase
      ?.from(table)
      ?.select(NOTE_SELECT)
      ?.eq(fk, entityId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return withAuthors(data || []);
  };

  /**
   * Compteurs + derniere note de toutes les entites d'un projet, en une
   * requete (plus une pour les noms des auteurs) : { [entityId]: { count, last } }.
   */
  const listSummaryByProject = async (projectId) => {
    if (!projectId) return {};
    const { data, error } = await supabase
      ?.from(table)
      ?.select(NOTE_SELECT)
      ?.eq('project_id', projectId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    const summary = {};
    (data || [])?.forEach((note) => {
      const entry = summary[note?.[fk]];
      if (entry) entry.count += 1;
      else summary[note?.[fk]] = { count: 1, last: note }; // tri desc : la 1re est la derniere
    });
    const names = await authorNames(Object.values(summary)?.map((s) => s?.last?.created_by));
    Object.values(summary)?.forEach((s) => { s.last = { ...s?.last, authorName: names?.[s?.last?.created_by] || null }; });
    return summary;
  };

  const create = async ({ entityId, projectId, body }) => {
    const text = cleanBody(body);
    const userId = await currentUserId();
    const { data, error } = await supabase
      ?.from(table)
      ?.insert({ [fk]: entityId, project_id: projectId, body: text, created_by: userId })
      ?.select(NOTE_SELECT)
      ?.single();
    if (error) throw error;
    return data;
  };

  // Garde « sa propre note » cote app : la note doit appartenir a l'utilisateur.
  const assertOwn = async (noteId) => {
    const userId = await currentUserId();
    const { data, error } = await supabase?.from(table)?.select('id, created_by')?.eq('id', noteId)?.single();
    if (error) throw error;
    if (!userId || data?.created_by !== userId) throw new Error('NOTE_NOT_OWN');
    return userId;
  };

  const update = async (noteId, body) => {
    const text = cleanBody(body);
    const userId = await assertOwn(noteId);
    const { data, error } = await supabase
      ?.from(table)
      ?.update({ body: text, updated_at: new Date()?.toISOString() })
      ?.eq('id', noteId)
      ?.eq('created_by', userId)
      ?.select(NOTE_SELECT)
      ?.single();
    if (error) throw error;
    return data;
  };

  const remove = async (noteId) => {
    const userId = await assertOwn(noteId);
    const { error } = await supabase?.from(table)?.delete()?.eq('id', noteId)?.eq('created_by', userId);
    if (error) throw error;
    return true;
  };

  return { list, listSummaryByProject, create, update, remove, getCurrentUserId: currentUserId };
};

export const extraNotesService = makeNotesService('extra_notes', 'extra_id');
export const directiveNotesService = makeNotesService('directive_notes', 'directive_id');

/** Service selon le type d'entite : 'extra' | 'directive'. */
export const notesServiceFor = (entityType) =>
  entityType === 'directive' ? directiveNotesService : extraNotesService;
